import type { Component } from 'solid-js'
import { createMemo, createSignal, For } from 'solid-js'
import { useTransContext } from '@mbarzda/solid-i18next'
import type { SectionMetrics } from '../../../features/engagement/domain/models/sections'
import Table, { type TableColumn, TableStatus } from '../../shared/components/Table'
import { formatDurationHours, formatDurationMinutes, SECTION_LABEL_KEYS } from './section-display'

type SortColumn = 'section' | 'visits' | 'unique_visitors' | 'seconds' | 'avg_seconds' | 'interactions'

interface SectionsTableProps {
  sections: SectionMetrics[]
  isLoading?: boolean
}

const TABLE_COLUMNS: TableColumn[] = [
  { id: 'section', sortable: true, labelKey: 'ls_sections_section' },
  { id: 'visits', sortable: true, labelKey: 'ls_sections_visits' },
  { id: 'interactions', sortable: true, labelKey: 'ls_sections_interactions' },
  { id: 'unique_visitors', sortable: true, labelKey: 'ls_sections_unique_visitors' },
  { id: 'avg_seconds', sortable: true, labelKey: 'ls_sections_avg_time_web' },
  { id: 'seconds', sortable: true, labelKey: 'ls_sections_total_time' }
]

/** Observed average seconds per web visit: pageview durations divided by visits */
const avgSecondsPerVisit = (section: SectionMetrics): number =>
  section.seconds.current / Math.max(1, section.visits.current)

const SectionsTable: Component<SectionsTableProps> = (props) => {
  const [t] = useTransContext()
  const [sortColumn, setSortColumn] = createSignal<SortColumn>('visits')
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>('desc')

  const tableOrderBy = () => `${sortColumn()}:${sortDirection()}`

  const setTableOrderBy = (orderBy: string | undefined): void => {
    // The Table component cycles desc -> asc -> undefined. Here we keep desc -> asc -> desc.
    if (!orderBy) {
      setSortDirection('desc')
      return
    }

    const [column, direction] = orderBy.split(':')
    if (column && (direction === 'asc' || direction === 'desc')) {
      setSortColumn(column as SortColumn)
      setSortDirection(direction)
    }
  }

  const sortValue = (section: SectionMetrics, column: SortColumn): number | string => {
    switch (column) {
      case 'section':
        return t(SECTION_LABEL_KEYS[section.section])
      case 'visits':
        return section.visits.current
      case 'unique_visitors':
        return section.uniqueVisitors.current
      case 'seconds':
        return section.seconds.current
      case 'avg_seconds':
        return avgSecondsPerVisit(section)
      case 'interactions':
        return section.interactions.current
    }
  }

  const sortedSections = createMemo(() => {
    const column = sortColumn()
    const factor = sortDirection() === 'asc' ? 1 : -1

    return [...props.sections].sort((a, b) => {
      const valueA = sortValue(a, column)
      const valueB = sortValue(b, column)
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * factor
      }
      return ((valueA as number) - (valueB as number)) * factor
    })
  })

  const status = () => {
    if (props.isLoading && props.sections.length === 0) {
      return TableStatus.Init
    }
    if (props.isLoading) {
      return TableStatus.Loading
    }
    if (props.sections.length === 0) {
      return TableStatus.NoData
    }
    return TableStatus.Loaded
  }

  return (
    <div class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <Table
          status={status}
          columns={TABLE_COLUMNS}
          orderBy={tableOrderBy}
          setOrderBy={setTableOrderBy}
          tableId="sections"
        >
          <For each={sortedSections()}>
            {(section) => (
              <tr class="hover:bg-gray-50">
                <td class="whitespace-nowrap px-4 py-3.5 text-sm font-medium text-gray-900">
                  {t(SECTION_LABEL_KEYS[section.section])}
                </td>
                <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">
                  {section.visits.current.toLocaleString('en-US')}
                </td>
                <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">
                  {section.interactions.current.toLocaleString('en-US')}
                </td>
                <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">
                  {section.uniqueVisitors.current.toLocaleString('en-US')}
                </td>
                <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">
                  {formatDurationMinutes(avgSecondsPerVisit(section))}
                </td>
                <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-700">
                  {formatDurationHours(section.seconds.current)}
                </td>
              </tr>
            )}
          </For>
        </Table>
      </div>
    </div>
  )
}

export default SectionsTable
